import { useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const COPIES = ['Original for recipient', 'Duplicate for transporter', 'Triplicate for supplier'];
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const qty = (v) => (v == null ? '' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 }));
const num = (v) => Number(v || 0);

/** Paper colours whatever the app's theme, so it looks and prints like an invoice. */
const STYLE = `
.tax-invoice { background:#fff; color:#111; font-size:12px; line-height:1.35; padding:16px; border:1px solid #222; max-width:900px; margin:0 auto; }
.tax-invoice table { width:100%; border-collapse:collapse; color:#111; }
.tax-invoice th, .tax-invoice td { border:1px solid #444; padding:3px 5px; vertical-align:top; }
.tax-invoice th { background:#f1f1f1; font-weight:600; }
.tax-invoice .ti-noborder td { border:none; padding:1px 0; }
.tax-invoice .ti-title { text-align:center; font-size:17px; font-weight:700; letter-spacing:.06em; margin:0; }
.tax-invoice .ti-copy { text-align:right; font-size:10.5px; font-style:italic; }
.tax-invoice .ti-name { font-size:16px; font-weight:700; }
.tax-invoice .ti-muted { color:#444; }
.tax-invoice .ti-label { font-size:10.5px; text-transform:uppercase; letter-spacing:.04em; color:#555; }
.tax-invoice .num { text-align:right; white-space:nowrap; }
.tax-invoice .ti-sign { height:60px; }
@media print { .tax-invoice { border:1px solid #222; max-width:none; } }
`;

/**
 * The printed tax invoice (GST rule 46): supplier and recipient with GSTINs and states, place of supply, each line's
 * HSN, quantity, unit, rate and tax at its rate, totals and the total in words, reverse charge, signature; with the bank
 * account, a UPI QR for what is due, and the terms. A composition dealer's is a bill of supply. Prints or saves as PDF.
 */
function TaxInvoice({ billId, currency = 'INR', data, forBuyer }) {
  const [inv, setInv] = useState(data || null);
  const [error, setError] = useState(null);
  const [copy, setCopy] = useState(COPIES[0]);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (v) => (v == null ? '' : f.format(num(v)));
  }, []);

  useEffect(() => {
    if (data) { setInv(data); return; } // already loaded (the buyer's own copy, opened with their order link)
    setInv(null);
    setError(null);
    axios.get(`/api/sale-bills/${billId}/print`)
      .then((r) => setInv(r.data))
      .catch(() => setError('Could not load the invoice'));
  }, [billId, data]);

  if (error) return <p className="text-danger">{error}</p>;
  if (!inv) return <div className="text-center py-4"><Spinner animation="border" /></div>;

  const s = inv.seller || {};
  const b = inv.buyer || {};
  const t = inv.totals || {};
  const igst = inv.interState;
  const taxCols = inv.composition ? 0 : igst ? 1 : 2;
  const currencySymbol = currency === 'INR' ? '₹' : `${currency} `;

  return (
    <div>
      <style>{STYLE}</style>
      {!forBuyer && (
        <div className="print-actions d-flex align-items-center gap-2 mb-2">
          <Form.Label className="small mb-0">Copy</Form.Label>
          <Form.Select size="sm" style={{ width: 'auto' }} value={copy} onChange={(e) => setCopy(e.target.value)}>
            {COPIES.map((c) => <option key={c}>{c}</option>)}
            <option value="">(no label)</option>
          </Form.Select>
          {inv.status !== 'ISSUED' && <span className="small text-warning">This bill is {String(inv.status).toLowerCase()}: issue it before sending.</span>}
        </div>
      )}

      <div className="tax-invoice">
        {copy && <div className="ti-copy">{copy}</div>}
        <p className="ti-title">{inv.title}</p>
        {inv.status === 'CANCELLED' && <p className="ti-title" style={{ color: '#b00' }}>CANCELLED</p>}

        <table style={{ marginTop: 8 }}>
          <tbody>
            <tr>
              <td style={{ width: '58%' }}>
                <div className="d-flex gap-2">
                  {s.logoUrl && <img src={s.logoUrl} alt="" style={{ maxHeight: 56, maxWidth: 110, objectFit: 'contain' }} />}
                  <div>
                    <div className="ti-name">{s.name}</div>
                    {s.legalName && <div>{s.legalName}</div>}
                    {s.address && <div className="ti-muted">{s.address}</div>}
                    {s.gstin && <div><b>GSTIN:</b> {s.gstin}</div>}
                    {s.state && <div><b>State:</b> {s.state}</div>}
                    {s.pan && <div><b>PAN:</b> {s.pan}</div>}
                    {(s.phone || s.email) && <div className="ti-muted">{[s.phone, s.email].filter(Boolean).join(' · ')}</div>}
                  </div>
                </div>
              </td>
              <td>
                <table className="ti-noborder">
                  <tbody>
                    <tr><td className="ti-label">Invoice no.</td><td><b>{inv.billNumber}</b></td></tr>
                    <tr><td className="ti-label">Date</td><td>{day(inv.billDate)}</td></tr>
                    {inv.dueDate && <tr><td className="ti-label">Due date</td><td>{day(inv.dueDate)}</td></tr>}
                    {inv.placeOfSupply && <tr><td className="ti-label">Place of supply</td><td>{inv.placeOfSupply}</td></tr>}
                    {!inv.composition && <tr><td className="ti-label">Reverse charge</td><td>{inv.reverseCharge ? 'Yes' : 'No'}</td></tr>}
                    {inv.salesOrderNumber && <tr><td className="ti-label">Order no.</td><td>{inv.salesOrderNumber}</td></tr>}
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td colSpan={b.shippingAddress ? 1 : 2}>
                <div className="ti-label">Billed to</div>
                <div><b>{b.name}</b></div>
                {b.billingAddress && <div className="ti-muted">{b.billingAddress}</div>}
                {b.gstin ? <div><b>GSTIN:</b> {b.gstin}</div> : <div className="ti-muted">Unregistered</div>}
                {b.state && <div><b>State:</b> {b.state}</div>}
                {b.phone && <div className="ti-muted">{b.phone}</div>}
              </td>
              {b.shippingAddress && (
                <td>
                  <div className="ti-label">Shipped to</div>
                  <div className="ti-muted">{b.shippingAddress}</div>
                </td>
              )}
            </tr>
          </tbody>
        </table>

        <table style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>#</th><th>Description</th><th>HSN/SAC</th><th className="num">Qty</th><th>Unit</th><th className="num">Rate</th>
              <th className="num">Disc.</th><th className="num">Taxable value</th>
              {taxCols > 0 && <th className="num">GST %</th>}
              {taxCols === 2 && <><th className="num">CGST</th><th className="num">SGST</th></>}
              {taxCols === 1 && <th className="num">IGST</th>}
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l) => (
              <tr key={l.sr}>
                <td>{l.sr}</td>
                <td>{l.description}</td>
                <td>{l.hsn || ''}</td>
                <td className="num">{qty(l.quantity)}</td>
                <td>{l.unit || ''}</td>
                <td className="num">{money(l.rate)}</td>
                <td className="num">{l.discount != null ? money(l.discount) : ''}</td>
                <td className="num">{money(l.taxable)}</td>
                {taxCols > 0 && <td className="num">{l.gstRate != null ? `${Number(l.gstRate)}%` : ''}</td>}
                {taxCols === 2 && <><td className="num">{money(l.cgst)}</td><td className="num">{money(l.sgst)}</td></>}
                {taxCols === 1 && <td className="num">{money(l.igst)}</td>}
                <td className="num">{money(l.total)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 600 }}>
              <td colSpan={3}>Total</td>
              <td className="num">{qty(t.quantity)}</td>
              <td colSpan={2} />
              <td className="num">{t.discount != null ? money(t.discount) : ''}</td>
              <td className="num">{money(t.taxable)}</td>
              {taxCols > 0 && <td />}
              {taxCols === 2 && <><td className="num">{money(t.cgst)}</td><td className="num">{money(t.sgst)}</td></>}
              {taxCols === 1 && <td className="num">{money(t.igst)}</td>}
              <td className="num">{money(t.total)}</td>
            </tr>
          </tbody>
        </table>

        <table style={{ marginTop: 8 }}>
          <tbody>
            <tr>
              <td style={{ width: '58%' }}>
                <div className="ti-label">Amount in words</div>
                <div><b>{t.totalInWords}</b></div>
                {taxCols > 0 && num(t.tax) > 0 && <div className="ti-muted" style={{ marginTop: 4 }}>Tax: {t.taxInWords}</div>}
              </td>
              <td>
                <table className="ti-noborder">
                  <tbody>
                    <tr><td>Taxable value</td><td className="num">{money(t.taxable)}</td></tr>
                    {taxCols === 2 && <><tr><td>CGST</td><td className="num">{money(t.cgst)}</td></tr><tr><td>SGST</td><td className="num">{money(t.sgst)}</td></tr></>}
                    {taxCols === 1 && <tr><td>IGST</td><td className="num">{money(t.igst)}</td></tr>}
                    <tr style={{ fontSize: 14, fontWeight: 700 }}><td>Total</td><td className="num">{currencySymbol}{money(t.total)}</td></tr>
                    {num(inv.balanceDue) > 0 && num(inv.balanceDue) !== num(t.total) && (
                      <tr><td>Balance due</td><td className="num">{currencySymbol}{money(inv.balanceDue)}</td></tr>
                    )}
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td>
                {inv.bank && (
                  <div>
                    <div className="ti-label">Bank details</div>
                    <div>{inv.bank.accountName}</div>
                    <div>{inv.bank.bankName}{inv.bank.branch ? `, ${inv.bank.branch}` : ''}</div>
                    <div>A/c no. <b>{inv.bank.accountNumber}</b>{inv.bank.ifsc ? <> · IFSC <b>{inv.bank.ifsc}</b></> : null}</div>
                  </div>
                )}
                {inv.upi && (
                  <div className="d-flex align-items-center gap-2" style={{ marginTop: inv.bank ? 6 : 0 }}>
                    <div style={{ background: '#fff', padding: 2 }}><QRCodeSVG value={inv.upi.uri} size={84} /></div>
                    <div>
                      <div className="ti-label">Scan to pay by UPI</div>
                      <div><b>{currencySymbol}{money(inv.balanceDue)}</b> to {inv.upi.id}</div>
                    </div>
                  </div>
                )}
                {inv.terms && (
                  <div style={{ marginTop: 6 }}>
                    <div className="ti-label">Terms and conditions</div>
                    <div style={{ whiteSpace: 'pre-line' }}>{inv.terms}</div>
                  </div>
                )}
                {inv.notes && <div style={{ marginTop: 6 }}><span className="ti-label">Notes </span>{inv.notes}</div>}
              </td>
              <td>
                <div style={{ textAlign: 'right' }}>For <b>{s.legalName || s.name}</b></div>
                <div className="ti-sign" />
                <div style={{ textAlign: 'right' }}>Authorised Signatory</div>
              </td>
            </tr>
          </tbody>
        </table>

        {inv.eInvoice && (
          <table style={{ marginTop: 8 }}>
            <tbody>
              <tr>
                <td style={{ width: 110 }}>{inv.eInvoice.signedQr && <QRCodeSVG value={inv.eInvoice.signedQr} size={96} />}</td>
                <td>
                  <div className="ti-label">e-Invoice</div>
                  <div style={{ wordBreak: 'break-all' }}><b>IRN:</b> {inv.eInvoice.irn}</div>
                  <div><b>Ack no.:</b> {inv.eInvoice.ackNo}{inv.eInvoice.ackDate ? ` · ${day(inv.eInvoice.ackDate)}` : ''}</div>
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {inv.composition && (
          <p style={{ margin: '8px 0 0' }}>Composition taxable person, not eligible to collect tax on supplies.</p>
        )}
        <p className="ti-muted" style={{ margin: '6px 0 0', textAlign: 'center', fontSize: 10.5 }}>This is a computer generated invoice.</p>
      </div>
    </div>
  );
}

export default TaxInvoice;
